import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { useAuth } from "../contexts/AuthContext";
import LoadingSpinner from "../components/LoadingSpinner";

function Settings() {
    const { accounts } = useMsal();
    const { user } = useAuth();
    const account = accounts[0];
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch("/api/v1/health")
            .then((r) => r.json())
            .then((data) => {
                setHealth(data);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    if (loading) return <LoadingSpinner message="Loading settings..." />;

    const claims = account?.idTokenClaims || {};
    const appRoles = user?.roles || [];
    const appPermissions = user?.permissions || [];

    return (
        <div>
            <div className="page-header">
                <h2>Settings</h2>
                <p>System configuration and status</p>
            </div>

            {/* User info */}
            <div className="card" style={{ marginBottom: 24 }}>
                <div className="card-title">Your Account</div>
                <table className="store-table">
                    <tbody>
                        <tr>
                            <td style={{ fontWeight: 600, width: 200 }}>Name</td>
                            <td>{account?.name || "\u2014"}</td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Email</td>
                            <td>{account?.username || "\u2014"}</td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Object ID</td>
                            <td style={{ fontSize: 13, color: "var(--text-muted)" }}>
                                {claims.oid || "\u2014"}
                            </td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Tenant</td>
                            <td style={{ fontSize: 13, color: "var(--text-muted)" }}>
                                {claims.tid || "\u2014"}
                            </td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Roles</td>
                            <td>
                                {appRoles.length > 0
                                    ? appRoles.map((r, i) => (
                                          <span
                                              key={i}
                                              style={{
                                                  background: "var(--accent)",
                                                  color: "#fff",
                                                  padding: "2px 8px",
                                                  borderRadius: 4,
                                                  fontSize: 12,
                                                  marginRight: 6,
                                              }}
                                          >
                                              {r.name}{r.scopeValue ? ` (${r.scopeValue})` : ""}
                                          </span>
                                      ))
                                    : "None assigned"}
                            </td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Permissions</td>
                            <td>
                                {appPermissions.length > 0
                                    ? appPermissions.map((p) => (
                                          <span
                                              key={p}
                                              style={{
                                                  background: "var(--bg-hover)",
                                                  padding: "2px 8px",
                                                  borderRadius: 4,
                                                  fontSize: 12,
                                                  marginRight: 6,
                                                  marginBottom: 4,
                                                  display: "inline-block",
                                              }}
                                          >
                                              {p}
                                          </span>
                                      ))
                                    : "No permissions"}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* System health */}
            <div className="card" style={{ marginBottom: 24 }}>
                <div className="card-title">System Status</div>
                <table className="store-table">
                    <tbody>
                        <tr>
                            <td style={{ fontWeight: 600, width: 200 }}>API Status</td>
                            <td>
                                <span
                                    style={{
                                        color: health?.status === "ok" ? "#22c55e" : "#ef4444",
                                        fontWeight: 600,
                                    }}
                                >
                                    {health?.status === "ok" ? "\u25CF Online" : "\u25CF Offline"}
                                </span>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Database</td>
                            <td>
                                <span
                                    style={{
                                        color: health?.database === "connected" ? "#22c55e" : "#ef4444",
                                        fontWeight: 600,
                                    }}
                                >
                                    {health?.database === "connected" ? "\u25CF Connected" : "\u25CF Disconnected"}
                                </span>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Total Stores</td>
                            <td>{health?.storeCount || 0}</td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Total Assessments</td>
                            <td>{health?.assessmentCount || 0}</td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Last Sync</td>
                            <td>
                                {health?.lastSync
                                    ? new Date(health.lastSync).toLocaleString()
                                    : "Never"}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Scan schedule */}
            <div className="card" style={{ marginBottom: 24 }}>
                <div className="card-title">Scan Configuration</div>
                <table className="store-table">
                    <tbody>
                        <tr>
                            <td style={{ fontWeight: 600, width: 200 }}>Scan Frequency</td>
                            <td>3{"\u00D7"} daily (7:00am, 11:00am, 3:00pm local time)</td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Zones per Store</td>
                            <td>6 (GD, PF, AW, FD, CD, PS)</td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>AI Model</td>
                            <td>Claude Haiku (BAU scans)</td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Camera System</td>
                            <td>Hikvision PTZ via ISAPI</td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Scan Server</td>
                            <td>On-premise (Point Cook)</td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Storage</td>
                            <td>Azure Blob (storeauditblob)</td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Database</td>
                            <td>Azure SQL (storeaudit-sql.database.windows.net)</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Role info */}
            <div className="card" style={{ marginBottom: 24 }}>
                <div className="card-title">Access Control</div>
                <p style={{ padding: "0 16px", color: "var(--text-muted)", fontSize: 13 }}>
                    Roles and permissions are managed via the Admin page. Users are auto-provisioned
                    on first login. RPL and State Manager roles are auto-detected from the areas sheet.
                </p>
            </div>

            {/* Version */}
            <div style={{ textAlign: "center", padding: 20, color: "var(--text-muted)", fontSize: 12 }}>
                Store Audit v0.2.0 \u2014 Built by Retail Systems Integration
            </div>
        </div>
    );
}

export default Settings;

